import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useCredit } from '@/hooks/useCredit';
import { getCommerceId } from '@/lib/ownership';
import { revenueCategoryFromOrderType } from '@/lib/financeSync';
import { recordInvoicePayment as recordPayment } from '@/lib/invoicePayments';
import { linkSupplierToCore } from '@/lib/zifekCore';
import {
  DEFAULT_ALERT_PREFS,
  accountBalance,
  sumExpenses,
  sumTransactions,
  type FinanceAccount,
  type FinanceAccountType,
  type FinanceAlertPrefs,
  type FinanceBudget,
  type FinanceExpense,
  type FinanceInvoice,
  type FinanceInvoiceItem,
  type FinanceRecurringExpense,
  type FinanceSettings,
  type FinanceSupplier,
  type FinanceSupplierTransaction,
  type FinanceTransaction,
  type InvoiceStatus,
  type TxDirection,
  type TxSource,
} from '@/lib/finance';

// Verrou global : évite de lancer plusieurs synchros simultanées quand
// plusieurs pages Finance montent en même temps.
let financeAutoSyncInFlight: Promise<unknown> | null = null;

export interface NewTransactionInput {
  direction: TxDirection;
  amount: number;
  account_id?: number | null;
  category?: string | null;
  description?: string | null;
  counterparty?: string | null;
  occurred_at?: string;
  source?: TxSource;
  source_ref?: string | null;
}

export interface NewExpenseInput {
  amount: number;
  category: string;
  account_id?: number | null;
  description?: string | null;
  vendor?: string | null;
  receipt_url?: string | null;
  occurred_at?: string;
}

export interface FinanceOrder {
  id: number;
  nomclient: string | null;
  email: string | null;
  tel: string | null;
  totalttc: string | number | null;
  sommepayee: string | number | null;
  monaie: string | null;
  date_time: string | null;
  user_id: string | null;
  statut_com_vendeur: string | null;
  details: string | null;
  typecommande: string | null;
}

function orderAmount(v: string | number | null | undefined): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export interface NewInvoiceInput {
  number?: string;
  doc_type?: 'invoice' | 'quote';
  customer_name: string;
  customer_phone?: string | null;
  customer_email?: string | null;
  customer_address?: string | null;
  customer_ice?: string | null;
  sale_date?: string | null;
  reference?: string | null;
  /** Devis d'origine quand une facture est générée depuis un devis. */
  source_quote_id?: number | null;
  payment_terms?: string | null;
  payment_method?: string | null;
  legal_mentions?: string | null;
  status?: InvoiceStatus;
  issue_date?: string;
  due_date?: string | null;
  discount?: number;
  tax_rate?: number;
  notes?: string | null;
  items: FinanceInvoiceItem[];
}

export function useFinance() {
  const { user } = useAuth();
  const merchantId = getCommerceId(user);
  const credit = useCredit();

  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [transactions, setTransactions] = useState<FinanceTransaction[]>([]);
  const [expenses, setExpenses] = useState<FinanceExpense[]>([]);
  const [invoices, setInvoices] = useState<FinanceInvoice[]>([]);
  const [suppliers, setSuppliers] = useState<FinanceSupplier[]>([]);
  const [supplierTransactions, setSupplierTransactions] = useState<FinanceSupplierTransaction[]>([]);
  const [budgets, setBudgets] = useState<FinanceBudget[]>([]);
  const [recurring, setRecurring] = useState<FinanceRecurringExpense[]>([]);
  const [settings, setSettings] = useState<FinanceSettings | null>(null);
  const [orders, setOrders] = useState<FinanceOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currency = settings?.currency || user?.monaie || 'MAD';

  const load = useCallback(async () => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [accRes, txRes, expRes, invRes, supRes, supTxRes, budRes, recRes, setRes, ordRes] = await Promise.all([
        supabase.from('finance_accounts').select('*').eq('merchant_id', merchantId).order('created_at'),
        supabase.from('finance_transactions').select('*').eq('merchant_id', merchantId).order('occurred_at', { ascending: false }),
        supabase.from('finance_expenses').select('*').eq('merchant_id', merchantId).order('occurred_at', { ascending: false }),
        supabase.from('finance_invoices').select('*').eq('merchant_id', merchantId).order('created_at', { ascending: false }),
        supabase.from('finance_suppliers').select('*').eq('merchant_id', merchantId).order('name'),
        supabase.from('finance_supplier_transactions').select('*').eq('merchant_id', merchantId).order('created_at', { ascending: false }),
        supabase.from('finance_budgets').select('*').eq('merchant_id', merchantId),
        supabase.from('finance_recurring_expenses').select('*').eq('merchant_id', merchantId),
        supabase.from('finance_settings').select('*').eq('merchant_id', merchantId).maybeSingle(),
        supabase.from('commande').select('*').eq('idvendeur', merchantId).order('date_time', { ascending: false }),
      ]);

      if (accRes.error) throw accRes.error;
      if (txRes.error) throw txRes.error;
      if (expRes.error) throw expRes.error;

      setAccounts((accRes.data as FinanceAccount[]) || []);
      setTransactions((txRes.data as FinanceTransaction[]) || []);
      setExpenses((expRes.data as FinanceExpense[]) || []);
      setInvoices((invRes.data as FinanceInvoice[]) || []);
      setSuppliers((supRes.data as FinanceSupplier[]) || []);
      setSupplierTransactions((supTxRes.data as FinanceSupplierTransaction[]) || []);
      setBudgets((budRes.data as FinanceBudget[]) || []);
      setRecurring((recRes.data as FinanceRecurringExpense[]) || []);
      setSettings((setRes.data as FinanceSettings) || null);
      setOrders((ordRes.data as FinanceOrder[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement des données financières');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Comptes ────────────────────────────────────────────────
  const accountBalances = useMemo(() => {
    const map: Record<number, number> = {};
    accounts.forEach((a) => {
      map[a.id] = accountBalance(a, transactions, expenses);
    });
    return map;
  }, [accounts, transactions, expenses]);

  const totalCash = useMemo(
    () => Object.values(accountBalances).reduce((s, v) => s + v, 0),
    [accountBalances]
  );

  const createAccount = useCallback(
    async (data: { name: string; account_type: FinanceAccountType; opening_balance?: number }) => {
      if (!merchantId) throw new Error('Non connecté');
      const { data: inserted, error: insErr } = await supabase
        .from('finance_accounts')
        .insert({
          merchant_id: merchantId,
          name: data.name,
          account_type: data.account_type,
          opening_balance: data.opening_balance ?? 0,
          currency,
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      setAccounts((prev) => [...prev, inserted as FinanceAccount]);
      return inserted as FinanceAccount;
    },
    [merchantId, currency]
  );

  const deleteAccount = useCallback(async (id: number) => {
    const { error: delErr } = await supabase.from('finance_accounts').delete().eq('id', id);
    if (delErr) throw delErr;
    setAccounts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // ── Transactions (revenus / dépenses de trésorerie) ────────
  const addTransaction = useCallback(
    async (input: NewTransactionInput): Promise<FinanceTransaction> => {
      if (!merchantId) throw new Error('Non connecté');
      const { data: inserted, error: insErr } = await supabase
        .from('finance_transactions')
        .insert({
          merchant_id: merchantId,
          account_id: input.account_id ?? null,
          direction: input.direction,
          amount: input.amount,
          currency,
          category: input.category ?? null,
          description: input.description ?? null,
          counterparty: input.counterparty ?? null,
          source: input.source ?? 'manual',
          source_ref: input.source_ref ?? null,
          occurred_at: input.occurred_at || new Date().toISOString(),
          created_by: merchantId,
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      const tx = inserted as FinanceTransaction;
      setTransactions((prev) => [tx, ...prev]);
      return tx;
    },
    [merchantId, currency]
  );

  /** Correction d'une transaction : on crée une écriture inverse (jamais de suppression). */
  const reverseTransaction = useCallback(
    async (tx: FinanceTransaction) => {
      if (!merchantId) throw new Error('Non connecté');
      const opposite: TxDirection = tx.direction === 'in' ? 'out' : 'in';
      const { data: inserted, error: insErr } = await supabase
        .from('finance_transactions')
        .insert({
          merchant_id: merchantId,
          account_id: tx.account_id,
          direction: opposite,
          amount: tx.amount,
          currency: tx.currency || currency,
          category: tx.category,
          description: `Correction : ${tx.description || 'transaction'}`,
          source: 'manual',
          source_ref: tx.source_ref,
          occurred_at: new Date().toISOString(),
          created_by: merchantId,
          reverses_transaction_id: tx.id,
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      setTransactions((prev) => [inserted as FinanceTransaction, ...prev]);
    },
    [merchantId, currency]
  );

  /** Transfert entre comptes : deux écritures équilibrées, sans impact sur le résultat. */
  const transfer = useCallback(
    async (fromAccountId: number, toAccountId: number, amount: number, note?: string) => {
      if (!merchantId) throw new Error('Non connecté');
      const now = new Date().toISOString();
      const { error: insErr } = await supabase.from('finance_transactions').insert([
        {
          merchant_id: merchantId, account_id: fromAccountId, direction: 'out', amount, currency,
          category: 'Transfert', description: note || 'Transfert sortant', source: 'manual', occurred_at: now, created_by: merchantId,
        },
        {
          merchant_id: merchantId, account_id: toAccountId, direction: 'in', amount, currency,
          category: 'Transfert', description: note || 'Transfert entrant', source: 'manual', occurred_at: now, created_by: merchantId,
        },
      ]);
      if (insErr) throw insErr;
      await load();
    },
    [merchantId, currency, load]
  );

  // ── Dépenses ───────────────────────────────────────────────
  const addExpense = useCallback(
    async (input: NewExpenseInput): Promise<FinanceExpense> => {
      if (!merchantId) throw new Error('Non connecté');
      const { data: inserted, error: insErr } = await supabase
        .from('finance_expenses')
        .insert({
          merchant_id: merchantId,
          account_id: input.account_id ?? null,
          category: input.category,
          amount: input.amount,
          currency,
          description: input.description ?? null,
          vendor: input.vendor ?? null,
          receipt_url: input.receipt_url ?? null,
          occurred_at: input.occurred_at || new Date().toISOString(),
          created_by: merchantId,
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      const exp = inserted as FinanceExpense;
      setExpenses((prev) => [exp, ...prev]);
      return exp;
    },
    [merchantId, currency]
  );

  const deleteExpense = useCallback(async (id: number) => {
    const { error: delErr } = await supabase.from('finance_expenses').delete().eq('id', id);
    if (delErr) throw delErr;
    setExpenses((prev) => prev.filter((e) => e.id !== id));
  }, []);

  // ── Dépenses récurrentes ───────────────────────────────────
  const addRecurring = useCallback(
    async (data: Omit<FinanceRecurringExpense, 'id' | 'merchant_id' | 'created_at'>) => {
      if (!merchantId) throw new Error('Non connecté');
      const { data: inserted, error: insErr } = await supabase
        .from('finance_recurring_expenses')
        .insert({ ...data, merchant_id: merchantId, currency })
        .select('*')
        .single();
      if (insErr) throw insErr;
      setRecurring((prev) => [...prev, inserted as FinanceRecurringExpense]);
    },
    [merchantId, currency]
  );

  const toggleRecurring = useCallback(async (id: number, isActive: boolean) => {
    const { error: upErr } = await supabase.from('finance_recurring_expenses').update({ is_active: isActive }).eq('id', id);
    if (upErr) throw upErr;
    setRecurring((prev) => prev.map((r) => (r.id === id ? { ...r, is_active: isActive } : r)));
  }, []);

  // ── Factures ───────────────────────────────────────────────
  const nextInvoiceNumber = useCallback(async (): Promise<string> => {
    const prefix = settings?.invoice_prefix || 'FAC';
    const year = new Date().getFullYear();
    const count = invoices.filter((i) => i.number.includes(String(year))).length + 1;
    return `${prefix}-${year}-${String(count).padStart(4, '0')}`;
  }, [settings, invoices]);

  const createInvoice = useCallback(
    async (input: NewInvoiceInput): Promise<FinanceInvoice> => {
      if (!merchantId) throw new Error('Non connecté');
      const number = input.number?.trim() || (await nextInvoiceNumber());
      // Total HT = somme des lignes nettes (quantité × prix − remise ligne).
      const subtotal = input.items.reduce((s, it) => {
        const gross = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
        return s + Math.max(0, gross - (Number(it.discount) || 0));
      }, 0);
      const discount = input.discount || 0;
      const taxRate = input.tax_rate || 0;
      const taxable = Math.max(0, subtotal - discount);
      const taxAmount = (taxable * taxRate) / 100;
      const total = taxable + taxAmount;
      const { data: inserted, error: insErr } = await supabase
        .from('finance_invoices')
        .insert({
          merchant_id: merchantId,
          number,
          doc_type: input.doc_type || 'invoice',
          customer_name: input.customer_name,
          customer_phone: input.customer_phone ?? null,
          customer_email: input.customer_email ?? null,
          customer_address: input.customer_address ?? null,
          customer_ice: input.customer_ice ?? null,
          sale_date: input.sale_date ?? null,
          reference: input.reference ?? null,
          source_quote_id: input.source_quote_id ?? null,
          payment_terms: input.payment_terms ?? null,
          payment_method: input.payment_method ?? null,
          legal_mentions: input.legal_mentions ?? null,
          status: input.status || 'draft',
          issue_date: input.issue_date || new Date().toISOString().slice(0, 10),
          due_date: input.due_date ?? null,
          currency,
          subtotal,
          discount,
          tax_rate: taxRate,
          tax_amount: taxAmount,
          total,
          amount_paid: 0,
          notes: input.notes ?? null,
          created_by: merchantId,
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      const invoice = inserted as FinanceInvoice;
      const items = input.items
        .filter((it) => it.description.trim())
        .map((it) => ({
          invoice_id: invoice.id,
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
          item_type: it.item_type || 'custom',
          discount: Number(it.discount) || 0,
          product_id: it.product_id ?? null,
          subtotal: Math.max(0, (Number(it.quantity) || 1) * (Number(it.unit_price) || 0) - (Number(it.discount) || 0)),
        }));
      if (items.length > 0) {
        await supabase.from('finance_invoice_items').insert(items);
      }
      invoice.items = items as FinanceInvoiceItem[];
      setInvoices((prev) => [invoice, ...prev]);
      return invoice;
    },
    [merchantId, currency, nextInvoiceNumber]
  );

  const updateInvoiceStatus = useCallback(async (id: number, status: InvoiceStatus) => {
    const patch: Record<string, unknown> = { status, updated_at: new Date().toISOString() };
    if (status === 'paid') {
      const inv = invoices.find((i) => i.id === id);
      if (inv) patch.amount_paid = inv.total;
    }
    const { error: upErr } = await supabase.from('finance_invoices').update(patch).eq('id', id);
    if (upErr) throw upErr;
    setInvoices((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status, ...(status === 'paid' ? { amount_paid: i.total } : {}) } : i))
    );
  }, [invoices]);

  /**
   * Enregistre un encaissement partiel (ou total) sur une facture.
   * Le montant payé est recalculé à partir des vrais encaissements.
   */
  const recordInvoicePayment = useCallback(
    async (invoice: FinanceInvoice, amount: number, paidAt: string, method?: string | null, note?: string | null) => {
      if (!merchantId) throw new Error('Non connecté');
      const result = await recordPayment({ invoice, merchantId, amount, paidAt, method, note });
      setInvoices((prev) =>
        prev.map((i) =>
          i.id === invoice.id ? { ...i, amount_paid: result.amountPaid, status: result.status } : i,
        ),
      );
      return result;
    },
    [merchantId],
  );

  const deleteInvoice = useCallback(async (id: number) => {
    await supabase.from('finance_invoice_items').delete().eq('invoice_id', id);
    await supabase.from('finance_invoice_payments').delete().eq('invoice_id', id);
    const { error: delErr } = await supabase.from('finance_invoices').delete().eq('id', id);
    if (delErr) throw delErr;
    setInvoices((prev) => prev.filter((i) => i.id !== id));
  }, []);

  // ── Fournisseurs ───────────────────────────────────────────
  const createSupplier = useCallback(
    async (data: Partial<FinanceSupplier>): Promise<FinanceSupplier> => {
      if (!merchantId) throw new Error('Non connecté');
      const { data: inserted, error: insErr } = await supabase
        .from('finance_suppliers')
        .insert({
          merchant_id: merchantId,
          name: data.name,
          phone: data.phone ?? null,
          email: data.email ?? null,
          address: data.address ?? null,
          notes: data.notes ?? null,
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      const supplier = inserted as FinanceSupplier;
      setSuppliers((prev) => [...prev, supplier].sort((a, b) => a.name.localeCompare(b.name)));
      // Centralise le fournisseur dans le socle Personnes (fiche contact unique).
      void linkSupplierToCore({
        id: supplier.id,
        merchant_id: merchantId,
        name: supplier.name,
        phone: supplier.phone,
        email: supplier.email,
        notes: supplier.notes,
      });
      return supplier;
    },
    [merchantId]
  );

  const deleteSupplier = useCallback(async (id: number) => {
    await supabase.from('finance_supplier_transactions').delete().eq('supplier_id', id);
    const { error: delErr } = await supabase.from('finance_suppliers').delete().eq('id', id);
    if (delErr) throw delErr;
    setSuppliers((prev) => prev.filter((s) => s.id !== id));
    setSupplierTransactions((prev) => prev.filter((t) => t.supplier_id !== id));
  }, []);

  const addSupplierTransaction = useCallback(
    async (input: { supplier_id: number; type: 'invoice' | 'payment'; amount: number; due_date?: string | null; description?: string | null; account_id?: number | null }) => {
      if (!merchantId) throw new Error('Non connecté');
      const { data: inserted, error: insErr } = await supabase
        .from('finance_supplier_transactions')
        .insert({
          merchant_id: merchantId,
          supplier_id: input.supplier_id,
          type: input.type,
          amount: input.amount,
          currency,
          due_date: input.due_date ?? null,
          description: input.description ?? null,
          account_id: input.account_id ?? null,
          status: input.type === 'payment' ? 'paid' : 'pending',
          created_by: merchantId,
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      setSupplierTransactions((prev) => [inserted as FinanceSupplierTransaction, ...prev]);
      // Un paiement fournisseur réduit la trésorerie du compte choisi.
      if (input.type === 'payment' && input.account_id) {
        await supabase.from('finance_transactions').insert({
          merchant_id: merchantId,
          account_id: input.account_id,
          direction: 'out',
          amount: input.amount,
          currency,
          category: 'Fournisseur',
          description: input.description || 'Paiement fournisseur',
          source: 'supplier',
          occurred_at: new Date().toISOString(),
          created_by: merchantId,
        });
        await load();
      }
    },
    [merchantId, currency, load]
  );

  const supplierBalance = useCallback(
    (supplierId: number): number => {
      const txs = supplierTransactions.filter((t) => t.supplier_id === supplierId);
      const inv = txs.filter((t) => t.type === 'invoice').reduce((s, t) => s + (Number(t.amount) || 0), 0);
      const pay = txs.filter((t) => t.type === 'payment').reduce((s, t) => s + (Number(t.amount) || 0), 0);
      return inv - pay;
    },
    [supplierTransactions]
  );

  // ── Réglages ───────────────────────────────────────────────
  const ensureSettings = useCallback(async (): Promise<FinanceSettings> => {
    if (!merchantId) throw new Error('Non connecté');
    if (settings?.id) return settings;
    const { data: inserted, error: insErr } = await supabase
      .from('finance_settings')
      .insert({
        merchant_id: merchantId,
        currency: user?.monaie || 'MAD',
        invoice_prefix: 'FAC',
        default_tax_rate: 0,
        alerts: DEFAULT_ALERT_PREFS,
      })
      .select('*')
      .single();
    if (insErr) throw insErr;
    const fresh = inserted as FinanceSettings;
    setSettings(fresh);
    return fresh;
  }, [merchantId, settings, user]);

  const saveSettings = useCallback(
    async (patch: Partial<FinanceSettings>) => {
      const current = settings?.id ? settings : await ensureSettings();
      const { error: upErr } = await supabase
        .from('finance_settings')
        .update({
          currency: patch.currency ?? current.currency,
          invoice_prefix: patch.invoice_prefix ?? current.invoice_prefix,
          default_tax_rate: patch.default_tax_rate ?? current.default_tax_rate,
          alerts: patch.alerts ?? current.alerts,
          updated_at: new Date().toISOString(),
        })
        .eq('id', current.id);
      if (upErr) throw upErr;
      setSettings({ ...current, ...patch } as FinanceSettings);
    },
    [settings, ensureSettings]
  );

  // ── Synchronisation des revenus (multi-sources) ─────────────
  // Rattache automatiquement à la trésorerie les revenus payés de TOUS
  // les modules : commandes (produits, produits digitaux, services,
  // formations, rendez-vous zcalendar) et réservations d'hébergement.
  // Chaque source est catégorisée puis écrite UNE SEULE fois grâce à
  // `source_ref` (idempotent, jamais de doublon).
  const syncAllRevenues = useCallback(async (): Promise<{ created: number; bySource: Record<string, number> }> => {
    if (!merchantId) return { created: 0, bySource: {} };

    const existing = new Set<string>();
    transactions
      .filter((t) => (t.source === 'order' || t.source === 'reservation') && t.source_ref != null)
      .forEach((t) => {
        const ref = String(t.source_ref);
        existing.add(ref);
        // Compatibilité avec l'ancien format (id brut) et le nouveau (préfixé).
        if (t.source === 'order') {
          existing.add(ref.startsWith('order:') ? ref.slice(6) : `order:${ref}`);
        }
      });
    const defaultAccount = accounts.find((a) => a.account_type === 'cash') || accounts[0] || null;

    const payload: Record<string, unknown>[] = [];
    const bySource: Record<string, number> = {};

    // 1. Commandes : produits, produits digitaux, services, formations, RDV.
    orders
      .filter((o) => orderAmount(o.sommepayee) > 0)
      .forEach((o) => {
        const ref = `order:${o.id}`;
        if (existing.has(ref) || existing.has(String(o.id))) return;
        const meta = revenueCategoryFromOrderType(o.typecommande);
        const amount = orderAmount(o.sommepayee);
        payload.push({
          merchant_id: merchantId,
          account_id: defaultAccount ? defaultAccount.id : null,
          direction: 'in' as TxDirection,
          amount,
          currency: o.monaie || currency,
          category: meta.label,
          description: `Commande #${o.id}${o.nomclient ? ` — ${o.nomclient}` : ''}`,
          source: 'order' as TxSource,
          source_ref: ref,
          counterparty: o.nomclient || null,
          occurred_at: o.date_time || new Date().toISOString(),
          created_by: merchantId,
        });
        bySource[meta.label] = (bySource[meta.label] || 0) + amount;
      });

    // 2. Réservations d'hébergement payées.
    try {
      const { data: resas } = await supabase
        .from('hebergement_reservations')
        .select('id, total, currency, guest_name, payment_status, created_at')
        .eq('merchant_id', merchantId);
      (resas || []).forEach((r: Record<string, unknown>) => {
        if ((r.payment_status as string) !== 'paid') return;
        const ref = `reservation:${r.id}`;
        if (existing.has(ref)) return;
        const amount = orderAmount(r.total as string | number | null);
        if (amount <= 0) return;
        payload.push({
          merchant_id: merchantId,
          account_id: defaultAccount ? defaultAccount.id : null,
          direction: 'in' as TxDirection,
          amount,
          currency: (r.currency as string) || currency,
          category: 'Hébergement',
          description: `Réservation #${r.id}${r.guest_name ? ` — ${r.guest_name}` : ''}`,
          source: 'reservation' as TxSource,
          source_ref: ref,
          counterparty: (r.guest_name as string) || null,
          occurred_at: (r.created_at as string) || new Date().toISOString(),
          created_by: merchantId,
        });
        bySource['Hébergement'] = (bySource['Hébergement'] || 0) + amount;
      });
    } catch {
      // L'hébergement est optionnel : on n'échoue pas la synchro si la table est absente.
    }

    if (payload.length === 0) return { created: 0, bySource };
    const { error: insErr } = await supabase.from('finance_transactions').insert(payload);
    if (insErr) throw insErr;
    await load();
    return { created: payload.length, bySource };
  }, [merchantId, orders, transactions, accounts, currency, load]);

  // ── Synchro automatique à l'ouverture de Finance ────────────
  // Dès que les données sont chargées, on rattache les revenus payés de
  // tous les modules à la trésorerie (une seule fois par page, sans doublon).
  const autoSyncRef = useRef(false);
  useEffect(() => {
    if (!merchantId || loading || autoSyncRef.current) return;
    autoSyncRef.current = true;
    if (financeAutoSyncInFlight) return;
    financeAutoSyncInFlight = syncAllRevenues()
      .catch(() => undefined)
      .finally(() => {
        financeAutoSyncInFlight = null;
      });
  }, [merchantId, loading, syncAllRevenues]);

  // Regroupement des revenus synchronisés par source (pour l'affichage).
  const revenueBySource = useMemo(() => {
    const map: Record<string, number> = {};
    transactions
      .filter((t) => t.direction === 'in' && (t.source === 'order' || t.source === 'reservation'))
      .forEach((t) => {
        const label = t.category || 'Autres revenus';
        map[label] = (map[label] || 0) + (Number(t.amount) || 0);
      });
    return map;
  }, [transactions]);

  const alertPrefs: FinanceAlertPrefs = settings?.alerts || DEFAULT_ALERT_PREFS;

  // ── Agrégats ───────────────────────────────────────────────
  const totalRevenueIn = useMemo(() => sumTransactions(transactions, 'in'), [transactions]);
  const totalExpensesOut = useMemo(() => sumExpenses(expenses), [expenses]);
  const receivables = credit.stats.totalOutstanding;
  const payables = useMemo(
    () => suppliers.reduce((s, sup) => s + Math.max(0, supplierBalance(sup.id)), 0),
    [suppliers, supplierBalance]
  );

  return {
    merchantId,
    currency,
    accounts,
    transactions,
    expenses,
    invoices,
    suppliers,
    supplierTransactions,
    budgets,
    recurring,
    orders,
    settings,
    alertPrefs,
    loading,
    error,
    reload: load,
    accountBalances,
    totalCash,
    totalRevenueIn,
    totalExpensesOut,
    receivables,
    payables,
    credit,
    createAccount,
    deleteAccount,
    addTransaction,
    reverseTransaction,
    transfer,
    addExpense,
    deleteExpense,
    addRecurring,
    toggleRecurring,
    createInvoice,
    updateInvoiceStatus,
    recordInvoicePayment,
    deleteInvoice,
    createSupplier,
    deleteSupplier,
    addSupplierTransaction,
    supplierBalance,
    revenueBySource,
    syncAllRevenues,
    ensureSettings,
    saveSettings,
  };
}